import React, { useState } from 'react';
import { Section, Card } from '../shared/primitives';
import { Segmented, Field, Result, Note, Bullets, AboutCard, References, SourceRow } from '../shared/protocolBits';
import {
  START, ADJUST, TRENDS, INSULIN_FACTS, INSULIN_NOTES, INSULIN_BACKGROUND, INSULIN_REFERENCES, INSULIN_PDF,
  startingRate, bandFor, trendFor,
} from '../../../data/hospitals/thiInsulin';

const ACCENT = '#9F8D70';
const PHASES = [
  { id: 'start', label: 'Starting an infusion' },
  { id: 'adjust', label: 'Adjusting the rate' },
];

const Discuss = () => (
  <span className="inline-block ml-1.5 align-middle px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
    discuss with attending
  </span>
);

const num = (v) => (v === '' ? NaN : parseFloat(v));

/** Intraoperative insulin infusion: calculator first, then the two tables and notes */
const InsulinGuide = () => {
  const [phase, setPhase] = useState('start');
  const [current, setCurrent] = useState('');
  const [previous, setPrevious] = useState('');

  const g = num(current);
  const p = num(previous);
  const hasG = Number.isFinite(g) && g > 0;

  const start = hasG ? bandFor(START, g) : null;
  const rate = hasG ? startingRate(g) : 0;
  const adj = hasG ? bandFor(ADJUST, g) : null;
  const trend = hasG && Number.isFinite(p) ? trendFor(g, p) : null;
  const trendLabel = trend ? TRENDS.find((t) => t.id === trend.id).label : null;
  const action = adj ? (adj.hold ? adj.action : trend ? adj.by[trend.id] : null) : null;

  return (
    <div className="space-y-6">
      <Segmented items={PHASES} value={phase} onChange={setPhase} />

      <Section title="Calculator">
        <Card>
          <div className="px-4 py-3 space-y-3">
            <Field label="Blood glucose" unit="mg/dL" value={current} onChange={setCurrent} placeholder="180" accent={ACCENT} />
            {phase === 'adjust' && (
              <Field label="Previous glucose" unit="mg/dL" value={previous} onChange={setPrevious} placeholder="210" accent={ACCENT} />
            )}
          </div>

          {phase === 'start' && start && (
            <>
              <Result label="Range" value={`${start.range} mg/dL`} />
              <Result
                label="Infusion"
                value={rate > 0 && start.max > 180 ? `${rate} units/hr` : start.infusion}
                detail={rate > 0 && start.max > 180 ? 'glucose ÷ 100, rounded to 0.5' : null}
                tone={start.max > 200 ? 'warn' : undefined}
              />
              <Result label="IV bolus" value={start.bolus || 'None'} tone={start.discuss ? 'warn' : undefined} />
              {start.discuss && <Note tone="warn">Discuss the bolus with the attending before giving it.</Note>}
            </>
          )}

          {phase === 'adjust' && adj && (
            <>
              <Result label="Range" value={`${adj.range} mg/dL`} />
              {trend && !adj.hold && (
                <Result label="Trend" value={trendLabel} detail={`${trend.change > 0 ? '+' : ''}${trend.change} mg/dL since last check`} tone={trend.id === 'downLarge' ? 'warn' : undefined} />
              )}
              {action
                ? <Result label="Action" value={action} tone={adj.hold ? 'danger' : undefined} />
                : <Note>Enter the previous glucose to get the rate change.</Note>}
              {(action || adj.hold) && <Result label="IV bolus" value={adj.bolus || 'None'} tone={adj.discuss ? 'warn' : undefined} />}
              {adj.discuss && action && <Note tone="warn">Discuss the bolus with the attending before giving it.</Note>}
            </>
          )}

          {!hasG && <Note>Enter a blood glucose to see the protocol's recommendation.</Note>}
        </Card>
      </Section>

      {phase === 'start' ? (
        <Section title="Starting an infusion">
          <Card>
            {START.map((b) => (
              <div key={b.range} className="px-4 py-3 flex items-start justify-between gap-4">
                <span className="text-[15px] font-medium text-gray-900 dark:text-white w-20 flex-shrink-0">{b.range}</span>
                <span className="flex-1 text-right">
                  <span className="block text-sm text-gray-700 dark:text-gray-300">{b.infusion}</span>
                  {b.bolus && <span className="block text-sm text-gray-700 dark:text-gray-300 mt-0.5">Bolus {b.bolus}<Discuss /></span>}
                </span>
              </div>
            ))}
          </Card>
        </Section>
      ) : (
        <Section title="Adjusting the rate">
          <Card>
            {ADJUST.map((b) => (
              <div key={b.range} className="px-4 py-3">
                <div className="text-[15px] font-medium text-gray-900 dark:text-white">{b.range} mg/dL</div>
                {b.hold ? (
                  <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{b.action}</p>
                ) : (
                  <dl className="mt-1 space-y-0.5">
                    {TRENDS.map((t) => (
                      <div key={t.id} className="flex justify-between gap-4 text-sm">
                        <dt className="text-gray-500 dark:text-gray-400">{t.label}</dt>
                        <dd className="text-right text-gray-700 dark:text-gray-300">{b.by[t.id]}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                {b.bolus && <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">Bolus {b.bolus}<Discuss /></p>}
              </div>
            ))}
          </Card>
        </Section>
      )}

      <Section title="Notes">
        <Card>
          <div className="px-4 py-3"><Bullets items={[...INSULIN_FACTS, ...INSULIN_NOTES]} /></div>
        </Card>
      </Section>

      <AboutCard title="About this protocol" accent={ACCENT}>
        {INSULIN_BACKGROUND.map((t) => <p key={t.slice(0, 24)}>{t}</p>)}
      </AboutCard>

      <References items={INSULIN_REFERENCES} />
      <SourceRow href={INSULIN_PDF} accent={ACCENT} />
    </div>
  );
};

export default InsulinGuide;
